import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Checkbox
} from '@mui/material';
import {
  Email,
  Forward,
  Group,
  Refresh,
  CheckCircle,
  Cancel,
  CallSplit,
  Edit,
  DeleteOutline
} from '@mui/icons-material';

const ExchangeMailboxInfo = ({ account, authenticatedFetch, API_ENDPOINT, isAdmin, onDataLoaded }) => {
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [cached, setCached] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Dialog state
  const [dlDialogOpen, setDlDialogOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [bulkAddInput, setBulkAddInput] = useState('');
  const [bulkAddLoading, setBulkAddLoading] = useState(false);
  const [bulkRemoveLoading, setBulkRemoveLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  useEffect(() => {
    if (account?.id) loadExchangeInfo();
  }, [account?.id, authenticatedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadExchangeInfo = async (forceRefresh = false) => {
    setLoading(true);
    setIsRefreshing(forceRefresh);
    setError(null);
    if (forceRefresh) setCached(false);
    try {
      const url = forceRefresh
        ? `${API_ENDPOINT}/accounts/${account.id}/exchange-info?refresh=true&_=${Date.now()}`
        : `${API_ENDPOINT}/accounts/${account.id}/exchange-info`;
      const response = await authenticatedFetch(url);
      const result = await response.json();
      if (result.success === false) {
        setError(result.error || 'Failed to load Exchange info');
      } else {
        setData(result);
        if (onDataLoaded) onDataLoaded(result);
        if (forceRefresh) {
          setCached(true);
          setTimeout(() => setCached(false), 5000);
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const parseEmails = (input) =>
    input.split(/[\n,]+/).map(e => e.trim()).filter(e => e.length > 0);

  const handleBulkAdd = async () => {
    const emails = parseEmails(bulkAddInput);
    if (!emails.length) return;
    setBulkAddLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/exchange-info/dl-members/bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dlAddress: data?.forwardingTarget?.PrimarySmtpAddress,
            memberEmails: emails
          })
        }
      );
      const result = await response.json();
      if (result.success) {
        setBulkResults({ type: 'add', results: result.results || [] });
        setSnackbar({ open: true, message: result.message || 'Members added', severity: 'success' });
        setBulkAddInput('');
        loadExchangeInfo();
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to add members', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to add members', severity: 'error' });
    } finally {
      setBulkAddLoading(false);
    }
  };

  const handleBulkRemove = async () => {
    if (!selectedMembers.length) return;
    setBulkRemoveLoading(true);
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/${account.id}/exchange-info/dl-members/bulk`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dlAddress: data?.forwardingTarget?.PrimarySmtpAddress,
            memberEmails: selectedMembers
          })
        }
      );
      const result = await response.json();
      if (result.success) {
        setBulkResults({ type: 'remove', results: result.results || [] });
        setSnackbar({ open: true, message: result.message || 'Members removed', severity: 'success' });
        setSelectedMembers([]);
        loadExchangeInfo();
      } else {
        setSnackbar({ open: true, message: result.error || 'Failed to remove members', severity: 'error' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed to remove members', severity: 'error' });
    } finally {
      setBulkRemoveLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDlDialogOpen(false);
    setSelectedMembers([]);
    setBulkAddInput('');
    setBulkResults(null);
  };

  const toggleMemberSelection = (email) => {
    setSelectedMembers(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    );
  };

  // ── Shared section header ────────────────────────────────────────────────
  const sectionHeader = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Email fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>Account Notification Recipients</Typography>
        </Box>
        {isAdmin && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {cached && (
              <Chip label="cached ✓" size="small" color="success" variant="outlined"
                sx={{ height: 16, fontSize: '0.6rem' }} />
            )}
            <Tooltip title="Refresh Exchange data (bypasses cache)">
              <IconButton onClick={() => loadExchangeInfo(true)} sx={{ p: 0.25 }}>
                <Refresh sx={{ fontSize: 13, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
      <Divider sx={{ mb: 0.75 }} />
      <Typography variant="caption" sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.5,
        color: 'text.disabled', fontSize: '0.72rem', mb: 1.25,
        bgcolor: 'action.hover', px: 0.75, py: 0.25, borderRadius: 1,
      }}>
        • Exchange Online
      </Typography>
    </>
  );

  if (loading) {
    return (
      <Box>
        {sectionHeader}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pl: 0.5 }}>
          <CircularProgress size={14} />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
            {isRefreshing ? 'Refreshing...' : 'Loading...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        {sectionHeader}
        <Alert severity="warning" sx={{ py: 0.5, fontSize: '0.75rem' }}
          action={
            <Tooltip title="Retry">
              <IconButton size="small" onClick={() => loadExchangeInfo(true)}>
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        >
          Exchange info unavailable
        </Alert>
      </Box>
    );
  }

  if (!data) return null;

  const { mailbox, forwardingSummary, forwardingTarget, dlMembers, account_email } = data;
  const hasForwarding = forwardingSummary?.hasInternalForwarding || forwardingSummary?.hasExternalForwarding;
  const hasDL = dlMembers && dlMembers.length > 0;

  return (
    <Box>
      {sectionHeader}

      {/* ── Two-column layout: left = mailbox+forwarding, right = DL members ── */}
      <Grid container spacing={2} alignItems="flex-start">

        {/* LEFT — Mailbox address + Forwarding details */}
        <Grid size={{ xs: 12, md: hasDL ? 4 : 12 }}>

          {/* Mailbox address */}
          {account_email && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75 }}>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, minWidth: 90, color: 'text.primary' }}>
                Mailbox:
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.82rem', wordBreak: 'break-all' }}>
                {account_email}
              </Typography>
            </Box>
          )}

          {/* Forwarding */}
          <Box sx={{ mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <Forward sx={{ fontSize: 14, color: hasForwarding ? 'warning.main' : 'text.disabled' }} />
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>Forwarding:</Typography>
              {hasForwarding ? (
                <Chip label="DL" size="small" color="warning" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
              ) : (
                <Chip label="None" size="small" color="default" variant="outlined" sx={{ height: 16, fontSize: '0.65rem' }} />
              )}
            </Box>

            {hasForwarding && forwardingTarget && (
              <Box sx={{ pl: 2.5, mt: 0.25 }}>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                  {forwardingTarget.DisplayName}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 0.25 }}>
                  {forwardingTarget.PrimarySmtpAddress}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {mailbox?.deliverToMailboxAndForward
                    ? <CheckCircle sx={{ fontSize: 12, color: 'success.main' }} />
                    : <Cancel sx={{ fontSize: 12, color: 'error.main' }} />
                  }
                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {mailbox?.deliverToMailboxAndForward ? 'Keeps copy in mailbox' : 'Does not keep copy'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        {/* RIGHT — DL Members multi-column grid */}
        {hasDL && (
          <Grid size={{ xs: 12, md: 8 }}>
            {/* DL header row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
              <Group fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600 }}>DL Members</Typography>
              <Chip label={dlMembers.length} size="small" sx={{ height: 16, fontSize: '0.65rem' }} />
              {isAdmin && (
                <Tooltip title="Manage DL members">
                  <IconButton size="small" onClick={() => setDlDialogOpen(true)} sx={{ p: 0.25, ml: 0.25 }}>
                    <Edit sx={{ fontSize: 13, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Multi-column member grid — auto-fills columns, no scroll */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: '2px 12px',
            }}>
              {dlMembers.map((member, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.4 }}>
                  {member.RecipientType === 'MailUniversalDistributionGroup'
                    ? <CallSplit sx={{ fontSize: 11, color: 'warning.main', mt: '3px', flexShrink: 0 }} />
                    : <Email sx={{ fontSize: 11, color: 'text.secondary', mt: '3px', flexShrink: 0 }} />
                  }
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 500, lineHeight: 1.3 }}>
                      {member.DisplayName}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.72rem', color: 'text.secondary', lineHeight: 1.2, wordBreak: 'break-all' }}>
                      {member.PrimarySmtpAddress}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Grid>
        )}
      </Grid>

      {/* ── Manage DL Members Dialog ── */}
      <Dialog open={dlDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '0.95rem', pb: 1 }}>
          Manage DL Members
          {forwardingTarget && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {forwardingTarget.DisplayName} — {forwardingTarget.PrimarySmtpAddress}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>

          {/* Current members with checkboxes */}
          {dlMembers && dlMembers.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
                Current Members
              </Typography>
              <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 220, overflow: 'auto' }}>
                {dlMembers.map((member, idx) => {
                  const email = member.PrimarySmtpAddress;
                  const isSelected = selectedMembers.includes(email);
                  const memberResult = bulkResults?.type === 'remove'
                    ? bulkResults.results?.find(r => r.member === email)
                    : null;
                  return (
                    <ListItem key={idx} disablePadding
                      sx={{ px: 1, py: 0.25, borderBottom: idx < dlMembers.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                      <Checkbox size="small" checked={isSelected} onChange={() => toggleMemberSelection(email)}
                        sx={{ p: 0.25, mr: 0.5 }} />
                      <ListItemText disableTypography
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {member.RecipientType === 'MailUniversalDistributionGroup'
                              ? <CallSplit sx={{ fontSize: 11, color: 'warning.main' }} />
                              : <Email sx={{ fontSize: 11, color: 'text.secondary' }} />
                            }
                            <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500, flex: 1 }}>
                              {member.DisplayName}
                            </Typography>
                            {memberResult && (
                              <Tooltip title={memberResult.success ? 'Removed' : (memberResult.error || 'Failed')}>
                                {memberResult.success
                                  ? <CheckCircle sx={{ fontSize: 13, color: 'success.main' }} />
                                  : <Cancel sx={{ fontSize: 13, color: 'error.main' }} />
                                }
                              </Tooltip>
                            )}
                          </Box>
                        }
                        secondary={
                          <Typography variant="body2" sx={{ fontSize: '0.7rem', color: 'text.secondary', pl: 1.75 }}>
                            {email}
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
              <Box sx={{ mt: 1 }}>
                <Button variant="outlined" color="error" size="small"
                  startIcon={bulkRemoveLoading ? <CircularProgress size={13} /> : <DeleteOutline />}
                  onClick={handleBulkRemove}
                  disabled={selectedMembers.length === 0 || bulkRemoveLoading}
                  sx={{ fontSize: '0.75rem' }}>
                  {bulkRemoveLoading ? 'Removing...' : `Remove Selected (${selectedMembers.length})`}
                </Button>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Add members textarea */}
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 0.5 }}>
              Add Members
            </Typography>
            <TextField multiline rows={4} fullWidth size="small"
              placeholder={"email1@example.com\nemail2@example.com"}
              value={bulkAddInput}
              onChange={(e) => setBulkAddInput(e.target.value)}
              disabled={bulkAddLoading}
              helperText="One email per line or comma-separated"
              sx={{ '& .MuiInputBase-input': { fontSize: '0.78rem' }, '& .MuiFormHelperText-root': { fontSize: '0.7rem' } }}
            />
            {bulkResults?.type === 'add' && bulkResults.results?.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {bulkResults.results.map((r, i) => (
                  <Tooltip key={i} title={r.success ? 'Added' : (r.error || 'Failed')}>
                    <Chip label={r.member} size="small"
                      color={r.success ? 'success' : 'error'} variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }} />
                  </Tooltip>
                ))}
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Button variant="contained" size="small"
                startIcon={bulkAddLoading ? <CircularProgress size={13} sx={{ color: 'inherit' }} /> : null}
                onClick={handleBulkAdd}
                disabled={!bulkAddInput.trim() || bulkAddLoading}
                sx={{ fontSize: '0.75rem' }}>
                {bulkAddLoading ? 'Adding...' : 'Add Members'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={handleCloseDialog} size="small">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity} sx={{ width: '100%', fontSize: '0.75rem' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExchangeMailboxInfo;
